/**
 * Smart date typing helpers (Enter to commit).
 * - "6" / "06"   → day / current month / current year
 * - "1010"       → 10/10/currentYear
 * - "10102026"   → 10/10/2026
 * Also accepts separators already present in the typed value.
 */
export const parseSmartDateInput = (raw, now = new Date()) => {
    if (raw == null) return null;
    const trimmed = String(raw).trim();
    if (!trimmed) return null;

    const digits = trimmed.replace(/\D/g, '');
    if (!digits) return null;

    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();

    let day;
    let month;
    let year;

    if (digits.length <= 2) {
        day = Number(digits);
        month = currentMonth;
        year = currentYear;
    } else if (digits.length === 3) {
        day = Number(digits.slice(0, 1));
        month = Number(digits.slice(1, 3));
        year = currentYear;
    } else if (digits.length === 4) {
        day = Number(digits.slice(0, 2));
        month = Number(digits.slice(2, 4));
        year = currentYear;
    } else if (digits.length === 6) {
        day = Number(digits.slice(0, 2));
        month = Number(digits.slice(2, 4));
        const yy = Number(digits.slice(4, 6));
        year = yy >= 70 ? 1900 + yy : 2000 + yy;
    } else if (digits.length === 8) {
        day = Number(digits.slice(0, 2));
        month = Number(digits.slice(2, 4));
        year = Number(digits.slice(4, 8));
    } else {
        return null;
    }

    if (!Number.isFinite(day) || !Number.isFinite(month) || !Number.isFinite(year)) return null;
    if (day < 1 || day > 31 || month < 1 || month > 12 || year < 1000 || year > 9999) return null;

    const date = new Date(year, month - 1, day);
    if (
        date.getFullYear() !== year
        || date.getMonth() !== month - 1
        || date.getDate() !== day
    ) {
        return null;
    }
    return date;
};

const YEAR_MIN = 1900;
const YEAR_MAX = 2099;

const expandTwoDigitYear = (yy) => {
    const n = Number(yy);
    if (!Number.isInteger(n) || n < 0 || n > 99) return null;
    return n >= 70 ? 1900 + n : 2000 + n;
};

/**
 * Smart month/year typing helpers (Enter to commit) for MM/YYYY fields.
 * - "6" / "06"     → 06/currentYear
 * - "0926"         → 09/2026
 * - "092026"       → 09/2026
 * Also accepts separators already present in the typed value.
 * Returns a Date at local month start (caller may convert to UTC).
 */
export const parseSmartMonthYearInput = (raw, now = new Date()) => {
    if (raw == null) return null;
    const trimmed = String(raw).trim();
    if (!trimmed) return null;

    const digits = trimmed.replace(/\D/g, '');
    if (!digits) return null;

    const currentYear = now.getFullYear();
    let month;
    let year;

    if (digits.length <= 2) {
        month = Number(digits);
        year = currentYear;
    } else if (digits.length === 3) {
        month = Number(digits.slice(0, 1));
        year = expandTwoDigitYear(digits.slice(1, 3));
    } else if (digits.length === 4) {
        month = Number(digits.slice(0, 2));
        year = expandTwoDigitYear(digits.slice(2, 4));
    } else if (digits.length === 5) {
        month = Number(digits.slice(0, 1));
        year = Number(digits.slice(1, 5));
    } else if (digits.length === 6) {
        month = Number(digits.slice(0, 2));
        year = Number(digits.slice(2, 6));
    } else {
        return null;
    }

    if (!Number.isFinite(month) || !Number.isFinite(year)) return null;
    if (month < 1 || month > 12 || year < YEAR_MIN || year > YEAR_MAX) return null;

    return new Date(year, month - 1, 1);
};
